<template>
  <div v-redirect-if-no-permission:ACCOUNTING :key="switch1">
    <loading-component :value="loading" />

    <!-- Desktop Table View -->
    <div class="desktop-view">
      <v-toolbar flat color="white">
        <v-toolbar-title>{{ $t("accountingPage.driversDonePaymentsTitle") }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-text-field 
          v-model="search" 
          append-icon="search" 
          :label='$t("accountingPage.search")'
          single-line 
          hide-details 
          @keyup.enter="getAccountingData" 
          @click:append="getAccountingData"
        ></v-text-field>
      </v-toolbar>

      <v-data-table :headers="headers" :items="accountingData" class="elevation-1" :pagination.sync="pagination"
        :rows-per-page-items="[5, 10, 15, 25, 100]" :loading="loading" :total-items="totalItems">
        <template v-slot:items="props">
          <td class="text-xs-start">{{ props.item.id }}</td>
          <td class="text-xs-start">{{ props.item.first_name }}</td>
          <td class="text-xs-start">{{ props.item.last_name }}</td>
          <td class="text-xs-start">{{ props.item.username }}</td>
          <td class="text-xs-start">{{ props.item.email }}</td>
          <td class="text-xs-start">{{ props.item.account_code || '-' }}</td>
          <td class="text-xs-start">{{ props.item.type == "internal" ? $t("accountingPage.typeInternal") : $t("accountingPage.typeExternal") }}</td>
          <td class="text-xs-start">{{ props.item.balance | money }}</td>
          <td class="text-xs-center">
            <v-btn 
              v-if="hasPermission('DRIVERS')"
              small 
              icon 
              @click="goToCaptainProfile(props.item)"
            >
              <v-icon>account_circle</v-icon>
            </v-btn>
          </td>
        </template>
      </v-data-table>
    </div>

    <!-- Mobile Card View -->
    <div class="mobile-view">
      <v-toolbar flat color="white">
       
        <v-text-field 
          v-model="search" 
          append-icon="search" 
          :label='$t("accountingPage.search")'
          single-line 
          hide-details 
          @keyup.enter="getAccountingData" 
          @click:append="getAccountingData"
        ></v-text-field>
      </v-toolbar>
      
      <div v-if="loading" class="text-center pa-4">
        
      </div>
      
      <div v-else-if="accountingData.length === 0" class="text-center pa-4">
        <b>{{ $t("accountingPage.noData") }}</b>
      </div>
      
      <div v-else class="cards-container">
        <v-card 
          v-for="item in accountingData" 
          :key="item.id" 
          class="mb-3 payment-card"
          elevation="2"
        >
          <v-card-text>
            <div class="card-row">
              <span class="card-label">#:</span>
              <span class="card-value">{{ item.id }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t("accountingPage.phone") }}:</span>
              <span class="card-value">{{ item.username || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t("accountingPage.email") }}:</span>
              <span class="card-value">{{ item.email || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t("accountingPage.name") }}:</span>
              <span class="card-value">{{ (item.first_name || '') + ' ' + (item.last_name || '') }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t("accountingPage.accountCode") }}:</span>
              <span class="card-value">{{ item.account_code || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t("accountingPage.type") }}:</span>
              <span class="card-value">{{ item.type == "internal" ? $t("accountingPage.typeInternal") : $t("accountingPage.typeExternal") }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t("accountingPage.balance") }}:</span>
              <span class="card-value balance" :class="item.balance >= 0 ? 'positive' : 'negative'">
                {{ (item.balance || 0) | money }}
              </span>
            </div>
          </v-card-text>
          <v-card-actions class="card-actions">
            <v-btn 
              v-if="hasPermission('DRIVERS')"
              small 
              icon 
              @click="goToCaptainProfile(item)"
            >
              <v-icon>account_circle</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
      
      <div v-if="accountingData.length > 0" class="pagination-container">
        <v-pagination
          v-model="pagination.page"
          :length="Math.ceil(totalItems / pagination.rowsPerPage)"
          @input="getAccountingData"
          :total-visible="5"
        ></v-pagination>
      </div>
    </div>

  </div>
</template>

<script>
import DataTable from "@/components/monaco/DataTable";
import moment from "moment";
import LoadingComponent from "@/components/LoadingComponent";

export default {
  name: "CaptainDonePayments",
  components: {
    LoadingComponent,
    DataTable
  },
  data() {
    return {
      accountingData: [],
      loading: false,
      search: '',
      totalItems: 0,
      pagination: {
        sortBy: "id",
        rowsPerPage: 25,
        page: 1,
      },
     
      switch1: null,
      isInitialLoad: true,
    };
  },
  computed: {
    headers() {
      return [
        { text: "#", value: "id" },
        { text: this.$t("accountingPage.firstName"), value: "first_name" },
        { text: this.$t("accountingPage.lastName"), value: "last_name" },
        { text: this.$t("accountingPage.phone"), value: "username" },
        { text: this.$t("accountingPage.email"), value: "email" },
        { text: this.$t("accountingPage.accountCode"), value: "account_code", sortable: false },
        { text: this.$t("accountingPage.type"), value: "type" },
        { text: this.$t("accountingPage.balance"), value: "balance" },
        { text: this.$t("accountingPage.actions"), value: "actions", sortable: false, align: "center" }
      ];
    },
  },
  mounted() {
    this.getAccountingData();
    // Set flag to false after initial load to allow watcher to work
    this.$nextTick(() => {
      this.isInitialLoad = false;
    });
  },

  watch: {
    pagination: {
      handler() {
        // Prevent watcher from triggering on initial mount
        if (!this.isInitialLoad) {
          this.getAccountingData();
        }
      },
      deep: true,
    },
  },
  methods: {
    goToCaptainProfile(item) {
      this.$router.push({
        path: "/captains/" + item.id,
        query: { tab: 3 }
      });
    },
    getAccountingData() {
      this.loading = true;
      const params = new URLSearchParams();
      params.append("perPage", String(this.pagination.rowsPerPage));
      params.append("page", this.pagination.page);
      params.append("sortBy", "id");
      params.append("descending", "false");
      if (this.search) {
        params.append("search", this.search);
      }

      this.axios
        .get("captain-balance", { params })
        .then(({ data }) => {

          const responseData = data.data || {};
          this.accountingData = responseData.data || [];
          this.totalItems = responseData.total || 0;
          if (responseData.per_page) {
            this.pagination.rowsPerPage = Number(responseData.per_page);
          }
        })
        .catch(() => {
          this.accountingData = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
/* Hide desktop table on mobile */
@media (max-width: 960px) {
  .desktop-view {
    display: none;
  }
}

/* Hide mobile cards on desktop */
@media (min-width: 961px) {
  .mobile-view {
    display: none;
  }
}

.cards-container {
  padding: 16px;
}

.payment-card {
  width: 100%;
}

.card-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.card-row:last-child {
  border-bottom: none;
}

.card-label {
  font-weight: 600;
  color: #666;
  font-size: 14px;
}

.card-value {
  color: #333;
  font-size: 14px;
  text-align: end;
}

.card-value.balance {
  font-weight: 700;
  font-size: 16px;
}

.card-value.balance.positive {
  color: #4caf50;
}

.card-value.balance.negative {
  color: #f44336;
}

.card-actions {
  padding: 8px 16px;
  justify-content: flex-end;
}

.pagination-container {
  padding: 16px;
  display: flex;
  justify-content: center;
}
</style>
